/* ioc-cro.js · CRO layer: gift-note toggle on the PDP, "Pick for me" (opens a new box in the builder), the cart's
   SUB-TOGGLE (one-time Box line <-> monthly plan, in place) and one-time shipping estimate by ZIP, the once-per-session
   question-first capture modal (exit intent or idle), and consent-gated marketing scripts via Shopify's Customer
   Privacy API. B-Commerce rebuild 2026-09-23. Nothing here fakes urgency: no timers, no stock counts, no invented
   social proof. */
(function () {
  function ready(fn) { if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn); else fn(); }

  /* ---------- cookie-banner guard (do not delete) ----------
     A native dialog.showModal() goes into the browser's TOP LAYER with a backdrop that swallows pointer events and
     traps focus. Shopify's hosted privacy banner is a plain <div id="shopify-pc__banner">, and the theme's own banner
     is <div id="ioc-cc">, so a guard that only looks for `dialog[open]` never fires and the capture modal lands on
     top of the consent prompt. An EEA or UK shopper is then unable to reach Accept or Decline. That is a consent
     failure and an accessibility failure at once, so the modal HOLDS while a banner is up or still owed.

     Note on the visibility test: both banners are position:fixed, and `offsetParent` is null for every fixed element,
     so an offsetParent check reports "not visible" even while the banner is on screen. Measure the box instead. */
  function onScreen(el) {
    if (!el || el.hidden) return false;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var st = window.getComputedStyle(el);
    return st.display !== "none" && st.visibility !== "hidden" && st.opacity !== "0";
  }

  function consentPending() {
    // 1. A banner that is actually painted right now, Shopify's or the theme's.
    if (onScreen(document.getElementById("shopify-pc__banner"))) return true;
    if (onScreen(document.querySelector(".shopify-pc__banner"))) return true;
    if (onScreen(document.getElementById("ioc-cc"))) return true;
    // 2. The official API: a banner is owed in this region and the visitor has not answered. This also covers the
    //    window before Shopify paints the banner, so the modal never wins a race against a late-arriving prompt.
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.shouldShowBanner === "function" && cp.shouldShowBanner() === true) return true;
    } catch (e) {}
    return false;
  }

  /* ---------- gift note (PDP block) ---------- */
  function initGiftNote() {
    document.querySelectorAll("[data-gift-note]").forEach(function (box) {
      if (box.dataset.bound) return;
      box.dataset.bound = "1";
      var toggle = box.querySelector("[data-gift-toggle]");
      var fields = box.querySelector("[data-gift-fields]");
      var note = box.querySelector("textarea");
      var count = box.querySelector("[data-gift-count]");
      if (!toggle || !fields) return;
      function sync() {
        var on = toggle.checked;
        fields.hidden = !on;
        fields.querySelectorAll("input,textarea").forEach(function (i) { i.disabled = !on; });
        toggle.setAttribute("aria-expanded", on ? "true" : "false");
        if (on) { var first = fields.querySelector("input,textarea"); if (first) first.focus(); }
      }
      toggle.addEventListener("change", sync);
      if (note && count) {
        var max = Number(note.getAttribute("maxlength") || 240);
        /* Final-B: a note with a line break is added as JSON (assets/ioc-shop.js), where a break stays one \n, so the
           counter, the limit and the textarea's own maxlength all count a break as one character. */
        var used = function (v) { return v.replace(/\r\n?/g, "\n").length; };
        var paint = function () {
          var v = note.value;
          while (v.length && used(v) > max) v = v.slice(0, -1);
          if (v !== note.value) note.value = v;
          count.textContent = used(v) + " / " + max;
        };
        note.addEventListener("input", paint);
        paint();
      }
      sync();
    });
  }

  /* ---------- cart gift note (drawer + cart page, above the checkout button) ----------
     9/28 call, Nick: "people a lot of times say, hey, I can't find the gift note." This step lives in
     snippets/cart-summary.liquid (rendered by both the drawer and the cart page), so it's the last thing before
     the checkout button on both surfaces. Unlike the PDP block (blocks/ioc-gift-note.liquid), which posts line-item
     properties through a product form, this saves CART ATTRIBUTES via /cart/update.js so one gift note covers the
     whole order, debounced so it doesn't fire on every keystroke. Read by the Order Printer gift-note-card template
     and the packing slip (convey-brain/clients/inside-out-cookie/shopify-launch), both fall back to the PDP's
     per-line properties when no cart attribute is set. */
  function initCartGiftNote() {
    document.querySelectorAll("[data-ioc-cart-gift]").forEach(function (box) {
      if (box.dataset.bound) return;
      box.dataset.bound = "1";
      var toggle = box.querySelector("[data-ioc-cart-gift-toggle]");
      var fields = box.querySelector("[data-ioc-cart-gift-fields]");
      var toInput = box.querySelector("[data-ioc-cart-gift-to]");
      var msgInput = box.querySelector("[data-ioc-cart-gift-message]");
      var fromInput = box.querySelector("[data-ioc-cart-gift-from]");
      var count = box.querySelector("[data-ioc-cart-gift-count]");
      if (!toggle || !fields) return;

      /* Same counting rule as the PDP note: a line break counts as one character, matching the textarea's own
         maxlength so the counter never disagrees with what actually gets typed. */
      var used = function (v) { return (v || "").replace(/\r\n?/g, "\n").length; };

      function paintCount() {
        if (!msgInput || !count) return;
        var max = Number(msgInput.getAttribute("maxlength") || 240);
        var v = msgInput.value;
        while (v.length && used(v) > max) v = v.slice(0, -1);
        if (v !== msgInput.value) msgInput.value = v;
        count.textContent = used(v) + " / " + max;
      }

      function setOpen(on) {
        fields.hidden = !on;
        [toInput, msgInput, fromInput].forEach(function (i) { if (i) i.disabled = !on; });
        toggle.setAttribute("aria-expanded", on ? "true" : "false");
        if (on && toInput) toInput.focus();
      }

      var pending = null;
      function sendUpdate(attributes) {
        if (pending) clearTimeout(pending);
        pending = setTimeout(function () {
          pending = null;
          fetch(cartRoot() + "cart/update.js", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: attributes })
          }).catch(function () {});
        }, 400);
      }

      function currentAttributes() {
        return {
          "Gift note to": toInput ? toInput.value : "",
          "Gift note message": msgInput ? msgInput.value : "",
          "Gift note from": fromInput ? fromInput.value : ""
        };
      }

      toggle.addEventListener("change", function () {
        var on = toggle.checked;
        setOpen(on);
        if (!on) {
          if (toInput) toInput.value = "";
          if (msgInput) msgInput.value = "";
          if (fromInput) fromInput.value = "";
          paintCount();
          sendUpdate({ "Gift note to": "", "Gift note message": "", "Gift note from": "" });
        }
      });

      [toInput, fromInput].forEach(function (i) {
        if (!i) return;
        i.addEventListener("input", function () { sendUpdate(currentAttributes()); });
      });
      if (msgInput) {
        msgInput.addEventListener("input", function () {
          paintCount();
          sendUpdate(currentAttributes());
        });
      }
      paintCount();
    });
  }

  /* ---------- duck pick (cart upsell): click a random native add-to-cart in the hidden pool ---------- */
  document.addEventListener("click", function (e) {
    var b = e.target.closest("[data-duck-pick]");
    if (!b) return;
    /* B-Commerce: cookies are sold by the box, so "Pick for me" never adds a loose cookie line. It opens the builder
       with one random retail flavor already in a new box. The pool (data-pool) is printed by snippets/ioc-cart-notes
       from menu-mode cookies only. */
    var pool = String(b.getAttribute("data-pool") || "").split(",").filter(Boolean);
    if (!pool.length) return;
    var base = b.getAttribute("data-builder") || "/pages/build-a-dozen";
    var pick = pool[Math.floor(Math.random() * pool.length)];
    b.setAttribute("aria-busy", "true");
    window.location.href = base + (base.indexOf("?") > -1 ? "&" : "?") + "add=" + encodeURIComponent(pick);
  });

  /* ---------- shared cart helpers (B-Commerce) ---------- */
  function cartRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/"; }
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll("cart-items-component[data-section-id], cart-icon[data-section-id]").forEach(function (el) {
      var id = el.getAttribute("data-section-id");
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }
  /* @shopify/events through an inline module script, which resolves the page's import map and is never minified
     (Final-B: Shopify's CDN minifier turned a dynamic import() here into require(), so every cart event was lost on
     the served theme). Shares the cache the builder uses (assets/ioc-shop.js), so the module is fetched once. */
  function eventsModule() {
    if (window.__iocEventsMod) return window.__iocEventsMod;
    window.__iocEventsMod = new Promise(function (resolve) {
      var done = false;
      window.__iocEventsDone = function (m) { if (done) return; done = true; resolve(m || null); };
      var s = document.createElement("script");
      s.type = "module";
      s.textContent = 'import("@shopify/events").then(function (m) { window.__iocEventsDone(m); }, function () { window.__iocEventsDone(null); });';
      document.head.appendChild(s);
      setTimeout(function () { window.__iocEventsDone(null); }, 8000);
    });
    return window.__iocEventsMod;
  }

  /* ---------- SUB-TOGGLE (CRO-SPEC 5.11): one-time Box line <-> the monthly plan, in place ----------
     POST /cart/change.js with the line key and the plan id (or null to go back to one-time), asking for the cart
     sections in the same request, then the deferred shopify:cart:lines-update Horizon's drawer, cart page, bubble and
     the subscriber free-shipping sync (snippets/ioc-subship.liquid) all listen for. Their re-render re-runs the
     mixed-cart shipping logic and prints the renewal disclosure under the switch. Off by default: the input is only
     checked when the line is already on the plan. */
  document.addEventListener("change", function (e) {
    var input = e.target && e.target.closest ? e.target.closest("[data-ioc-subtoggle-input]") : null;
    if (!input) return;
    var box = input.closest("[data-ioc-subtoggle]");
    if (!box || box.classList.contains("is-busy")) return;
    var on = input.checked;
    var err = box.querySelector("[data-ioc-subtoggle-err]");
    if (err) { err.hidden = true; err.textContent = ""; }
    box.classList.add("is-busy");
    var body = { id: box.getAttribute("data-key"), quantity: Number(box.getAttribute("data-qty") || 1), selling_plan: on ? Number(box.getAttribute("data-plan")) : null };
    var secs = cartSectionIds();
    if (secs.length) { body.sections = secs.join(","); body.sections_url = window.location.pathname; }
    eventsModule().then(function (ev) {
      var Ev = ev && ev.CartLinesUpdateEvent;
      var deferred = Ev ? Ev.createPromise() : null;
      /* Final-B: the event names the line it changes. An empty lines array fails Shopify's standard-events check. */
      if (Ev) input.dispatchEvent(new Ev({ action: "update", context: "cart", lines: [{ id: body.id, quantity: body.quantity }], promise: deferred.promise }));
      return fetch(cartRoot() + "cart/change.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          var j = res.json || {};
          var failed = !res.ok || j.status;
          if (failed) throw new Error(j.description || j.message || "That did not work. Try again, or change it at checkout.");
          /* Shopify can return null for a section it could not render in time. Then fetch them once more ourselves;
             if they still do not come back, reload so the shopper never sees a stale line or total. */
          var complete = function (sections) { return !!sections && secs.every(function (id) { return !!sections[id]; }); };
          var sectionsP = complete(j.sections) ? Promise.resolve(j.sections)
            : fetch(window.location.pathname + "?sections=" + encodeURIComponent(secs.join(",")), { credentials: "same-origin" })
                .then(function (r) { return r.ok ? r.json() : null; })
                .catch(function () { return null; });
          return sectionsP.then(function (sections) {
            if (!deferred || !complete(sections)) { window.location.reload(); return; }
            var detail = { items: j.items, source: "ioc-subtoggle", itemCount: j.item_count, sections: sections };
            deferred.resolve({ cart: Ev.createCartFromAjaxResponse ? Ev.createCartFromAjaxResponse(j) : j, detail: detail });
          });
        })
        .catch(function (error) {
          if (deferred) deferred.reject(error);
          input.checked = !on;
          box.classList.remove("is-busy");
          if (err) { err.textContent = (error && error.message) || "That did not work. Try again."; err.hidden = false; }
        });
    });
  });

  /* ---------- D2-A (2026-09-23, Buggy P1-3): a box with premium flavors and its add-on move together ----------
     [data-ioc-box-remove]   one Remove on a premium box: /cart/update.js zeroes the box and its add-on line in ONE
                             request (data-updates, printed by snippets/cart-products.liquid).
     [data-ioc-premium-fix]  the guard's "Fix it" (snippets/ioc-cart-notes.liquid): /cart/update.js sets the add-on
                             lines to match the boxes, then /cart/add.js adds any add-on a box is missing.
     Both re-render the cart sections through the same deferred shopify:cart:lines-update the SUB-TOGGLE uses. */
  function cartPost(path, body) {
    return fetch(cartRoot() + path, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (j) { if (!r.ok || j.status) throw new Error(j.description || j.message || "That did not work. Try again."); return j; }); });
  }
  function cartMutate(origin, steps) {
    var secs = cartSectionIds();
    return eventsModule().then(function (ev) {
      var Ev = ev && ev.CartLinesUpdateEvent;
      /* Final-B: the event carries the lines it changes (an empty lines array fails Shopify's standard-events check):
         the updated line keys, or, when the steps only add, the added variants. With nothing to name it is not sent,
         and the page reloads below instead. A line set to 0 makes it a "remove" (an "update" needs every quantity
         above 0), the same split Horizon's own cart uses. */
      var lines = [], action = "update";
      steps.forEach(function (st) {
        var u = st.body && st.body.updates;
        if (u) Object.keys(u).forEach(function (k) { lines.push({ id: k, quantity: Number(u[k]) || 0 }); });
      });
      if (lines.some(function (l) { return l.quantity === 0; })) action = "remove";
      if (!lines.length) {
        action = "add";
        steps.forEach(function (st) {
          ((st.body && st.body.items) || []).forEach(function (i) { lines.push({ merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 }); });
        });
      }
      var deferred = Ev && lines.length ? Ev.createPromise() : null;
      if (deferred) origin.dispatchEvent(new Ev({ action: action, context: "cart", lines: lines, promise: deferred.promise }));
      var chain = Promise.resolve(null);
      steps.forEach(function (st) { chain = chain.then(function () { return cartPost(st.path, st.body); }); });
      return chain
        .then(function () { return fetch(cartRoot() + "cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }); })
        .then(function (cart) {
          var sectionsP = secs.length
            ? fetch(window.location.pathname + "?sections=" + encodeURIComponent(secs.join(",")), { credentials: "same-origin" }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
            : Promise.resolve({});
          return sectionsP.then(function (sections) {
            var complete = !!sections && secs.every(function (id) { return !!sections[id]; });
            if (!deferred || !complete) { window.location.reload(); return; }
            deferred.resolve({ cart: Ev.createCartFromAjaxResponse ? Ev.createCartFromAjaxResponse(cart) : cart, detail: { items: cart.items, source: "ioc-premium", itemCount: cart.item_count, sections: sections } });
          });
        })
        .catch(function (error) { if (deferred) deferred.reject(error); throw error; });
    });
  }
  document.addEventListener("click", function (e) {
    var rm = e.target.closest && e.target.closest("[data-ioc-box-remove]");
    var fix = !rm && e.target.closest && e.target.closest("[data-ioc-premium-fix]");
    var el = rm || fix;
    if (!el || el.getAttribute("aria-busy") === "true") return;
    e.preventDefault();
    var updates = {}, adds = [];
    try { updates = JSON.parse(el.getAttribute("data-updates") || "{}"); } catch (err) {}
    try { adds = JSON.parse(el.getAttribute("data-adds") || "[]"); } catch (err) {}
    var steps = [];
    if (Object.keys(updates).length) steps.push({ path: "cart/update.js", body: { updates: updates } });
    if (adds.length) steps.push({ path: "cart/add.js", body: { items: adds } });
    if (!steps.length) return;
    el.setAttribute("aria-busy", "true");
    el.disabled = true;
    var row = rm && el.closest("tr");
    if (row) row.classList.add("is-busy");
    cartMutate(el, steps).catch(function () {
      el.removeAttribute("aria-busy");
      el.disabled = false;
      if (row) row.classList.remove("is-busy");
      window.location.reload();
    });
  });

  /* USPS ZIP prefix (first three digits) -> state, for the rate estimate below. [from, to, state] */
  var ZIP3 = [[5,5,"New York"],[10,27,"Massachusetts"],[28,29,"Rhode Island"],[30,38,"New Hampshire"],[39,49,"Maine"],
    [50,54,"Vermont"],[55,55,"Massachusetts"],[56,59,"Vermont"],[60,69,"Connecticut"],[70,89,"New Jersey"],[100,149,"New York"],
    [150,196,"Pennsylvania"],[197,199,"Delaware"],[200,200,"District of Columbia"],[201,201,"Virginia"],[202,205,"District of Columbia"],
    [206,219,"Maryland"],[220,246,"Virginia"],[247,268,"West Virginia"],[270,289,"North Carolina"],[290,299,"South Carolina"],
    [300,319,"Georgia"],[320,349,"Florida"],[350,369,"Alabama"],[370,385,"Tennessee"],[386,397,"Mississippi"],[398,399,"Georgia"],
    [400,427,"Kentucky"],[430,459,"Ohio"],[460,479,"Indiana"],[480,499,"Michigan"],[500,528,"Iowa"],[530,549,"Wisconsin"],
    [550,567,"Minnesota"],[569,569,"District of Columbia"],[570,577,"South Dakota"],[580,588,"North Dakota"],[590,599,"Montana"],
    [600,629,"Illinois"],[630,658,"Missouri"],[660,679,"Kansas"],[680,693,"Nebraska"],[700,714,"Louisiana"],[716,729,"Arkansas"],
    [730,732,"Oklahoma"],[733,733,"Texas"],[734,749,"Oklahoma"],[750,799,"Texas"],[800,816,"Colorado"],[820,831,"Wyoming"],
    [832,838,"Idaho"],[840,847,"Utah"],[850,865,"Arizona"],[870,884,"New Mexico"],[885,885,"Texas"],[889,898,"Nevada"],
    [900,961,"California"],[967,968,"Hawaii"],[970,979,"Oregon"],[980,994,"Washington"],[995,999,"Alaska"]];
  function zipState(zip) {
    var p = Number(String(zip).slice(0, 3));
    for (var i = 0; i < ZIP3.length; i++) if (p >= ZIP3[i][0] && p <= ZIP3[i][1]) return ZIP3[i][2];
    return "";
  }

  /* ---------- one-time shipping estimate by ZIP (CRO-SPEC 4.9.1) ----------
     Shopify's own rates for THIS cart: POST prepare_shipping_rates, then poll async_shipping_rates. Prints the rates
     Shopify returns (name + price, the same list checkout shows), or says it could not get one. No dates, no guesses. */
  document.addEventListener("submit", function (e) {
    var f = e.target && e.target.closest ? e.target.closest("[data-ioc-shipest]") : null;
    if (!f) return;
    e.preventDefault();
    var out = f.querySelector("[data-ioc-shipest-out]");
    var zipIn = f.querySelector("input[name='zip']");
    var zip = String((zipIn || {}).value || "").trim();
    /* Ship Gate a11y (WCAG 3.3.1): a bad ZIP marks the field invalid and ties the message to it; a good one clears both. */
    if (!out.id && zipIn) out.id = zipIn.id + "-msg";
    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      out.textContent = "Enter a 5 digit US ZIP code.";
      if (zipIn) { zipIn.setAttribute("aria-invalid", "true"); zipIn.setAttribute("aria-describedby", out.id); zipIn.focus(); }
      return;
    }
    if (zipIn) { zipIn.removeAttribute("aria-invalid"); zipIn.removeAttribute("aria-describedby"); }
    /* Shopify's rate endpoint also needs the state; the first three digits of a US ZIP decide it (USPS prefixes). */
    var state = zipState(zip);
    if (!state) { out.textContent = "We could not place that ZIP. Checkout will show the rate once you enter your address."; return; }
    out.textContent = "Getting rates…";
    var q = "shipping_address%5Bzip%5D=" + encodeURIComponent(zip) + "&shipping_address%5Bcountry%5D=United%20States&shipping_address%5Bprovince%5D=" + encodeURIComponent(state);
    var tries = 0;
    function paint(rates) {
      if (!rates || !rates.length) { out.textContent = "No rate came back for that ZIP. Checkout will show it once you enter your address."; return; }
      out.innerHTML = "";
      var ul = document.createElement("ul");
      rates.forEach(function (r) {
        var li = document.createElement("li");
        var n = Number(r.price);
        li.textContent = r.name + " · " + (n === 0 ? "Free" : "$" + n.toFixed(2));
        ul.appendChild(li);
      });
      out.appendChild(ul);
    }
    function poll() {
      tries++;
      fetch(cartRoot() + "cart/async_shipping_rates.json?" + q, { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(function (r) { return r.status === 200 ? r.json() : null; })
        .then(function (j) {
          if (j && j.shipping_rates) return paint(j.shipping_rates);
          if (tries < 8) return setTimeout(poll, 900);
          paint(null);
        })
        .catch(function () { paint(null); });
    }
    fetch(cartRoot() + "cart/prepare_shipping_rates.json?" + q, { method: "POST", credentials: "same-origin" })
      .then(function (r) {
        if (r.status >= 400) return r.json().then(function (j) { var m = j && (j.zip || j.error || j.country); out.textContent = m ? "That ZIP did not work: " + [].concat(m).join(" ") : "No rate came back for that ZIP."; });
        setTimeout(poll, 600);
      })
      .catch(function () { paint(null); });
  });

  /* ---------- House Account waitlist: a tier button with no membership product opens the capture modal ---------- */
  document.addEventListener("click", function (e) {
    var a = e.target.closest("[data-ioc-waitlist]");
    if (!a) return;
    var dlg = document.getElementById("iocCapture");
    // No modal, another dialog already open, or a cookie banner on screen: follow the href to the email section.
    if (!dlg || typeof dlg.showModal !== "function" || document.querySelector("dialog[open]") || consentPending()) return;
    e.preventDefault();
    try { window.sessionStorage.setItem("ioc-capture", "1"); } catch (err) {}
    if (typeof dlg.__iocAnswer === "function") dlg.__iocAnswer("me", true);
    dlg.showModal();
    var input = dlg.querySelector("input[type='email']");
    if (input) input.focus();
  });

  /* ---------- capture modal (CRO-SPEC 4.1.5): question first, then the email ----------
     "Who are the cookies for?" Me / A gift / The office / A fundraiser. The answer is saved for the session, written
     onto the Shopify customer as a tag next to "newsletter", and sent to Klaviyo when its onsite script is on the page.
     After the form posts, the success state offers one route by the answer. Never while the builder holds picks. */
  var CAP_ROUTES = {
    me: { tag: "cookies-for-me", kicker: "For you. Good.", href: "/pages/build-a-dozen", label: "Build your box" },
    gift: { tag: "cookies-for-gift", kicker: "A gift. Nice of you.", href: "/pages/gifting", label: "See how gifting works" },
    office: { tag: "cookies-for-office", kicker: "For the office.", href: "/pages/corporate", label: "Office and corporate orders" },
    fundraiser: { tag: "cookies-for-fundraiser", kicker: "For a fundraiser.", href: "/pages/fundraisers", label: "How fundraisers work" }
  };
  function boxHasPicks() {
    try {
      var s = JSON.parse(window.sessionStorage.getItem("ioc-box") || "null");
      return !!(s && Array.isArray(s.picks) && s.picks.length);
    } catch (e) { return false; }
  }
  function initCapture() {
    var dlg = document.getElementById("iocCapture");
    if (!dlg || typeof dlg.showModal !== "function") return;
    var delay = Math.max(10, Number(dlg.getAttribute("data-delay") || 30)) * 1000;
    var posted = dlg.hasAttribute("data-posted");
    var form = dlg.querySelector("form") || dlg.closest("form");
    var joined = false, shown = false, submitting = false;
    try { joined = window.localStorage.getItem("ioc-list") === "yes"; } catch (e) {}
    try { shown = window.sessionStorage.getItem("ioc-capture") === "1"; } catch (e) {}
    try { submitting = window.sessionStorage.getItem("ioc-capture-submit") === "1"; } catch (e) {}

    function close() { if (dlg.open) dlg.close(); }
    dlg.querySelectorAll("[data-capture-close]").forEach(function (x) { x.addEventListener("click", close); });
    dlg.addEventListener("click", function (e) { if (e.target === dlg) close(); });
    dlg.addEventListener("cancel", function () { try { window.sessionStorage.setItem("ioc-capture", "1"); } catch (e) {} });

    var askStep = dlg.querySelector("[data-capture-step='ask']");
    var emailStep = dlg.querySelector("[data-capture-step='email']");
    var tags = dlg.querySelector("[data-capture-tags]");
    var kicker = dlg.querySelector("[data-capture-kicker]");
    function answer(key, skipFocus) {
      var r = CAP_ROUTES[key] || CAP_ROUTES.me;
      try { window.sessionStorage.setItem("ioc-capture-for", key); } catch (e) {}
      if (tags) tags.value = "newsletter, " + r.tag;
      if (kicker) kicker.textContent = r.kicker;
      if (askStep) askStep.hidden = true;
      if (emailStep) emailStep.hidden = false;
      if (!skipFocus) { var em = dlg.querySelector("input[type='email']"); if (em) em.focus(); }
    }
    dlg.__iocAnswer = answer;
    dlg.querySelectorAll("[data-capture-answer]").forEach(function (b) {
      b.addEventListener("click", function () { answer(b.getAttribute("data-capture-answer")); });
    });
    var back = dlg.querySelector("[data-capture-back]");
    if (back) back.addEventListener("click", function () {
      if (emailStep) emailStep.hidden = true;
      if (askStep) askStep.hidden = false;
      var first = dlg.querySelector("[data-capture-answer]");
      if (first) first.focus();
    });
    if (form) form.addEventListener("submit", function () {
      try { window.sessionStorage.setItem("ioc-capture-submit", "1"); } catch (e) {}
      /* Klaviyo, only when its onsite script is already on the page (consent-gated in initConsent below). */
      try {
        var key = window.sessionStorage.getItem("ioc-capture-for") || "";
        var em = (dlg.querySelector("input[type='email']") || {}).value;
        if (em && key && window.klaviyo && typeof window.klaviyo.identify === "function") window.klaviyo.identify({ email: em, "Cookies for": key });
      } catch (e) {}
    });

    if (posted) {
      // The customer form reloads the page with the success state rendered. Only re-open if it was OUR form.
      try { window.localStorage.setItem("ioc-list", "yes"); window.sessionStorage.removeItem("ioc-capture-submit"); } catch (e) {}
      var route = dlg.querySelector("[data-capture-route]");
      var key = "";
      try { key = window.sessionStorage.getItem("ioc-capture-for") || ""; } catch (e) {}
      if (route && CAP_ROUTES[key]) {
        route.setAttribute("href", CAP_ROUTES[key].href);
        route.textContent = CAP_ROUTES[key].label;
        route.hidden = false;
      }
      if (submitting) dlg.showModal();
      return;
    }
    if (joined || shown) return;

    var timer;
    function open() {
      if (shown || dlg.open) return;
      if (document.querySelector("dialog[open]")) { arm(); return; } // never stack on the cart drawer or search
      if (consentPending()) { arm(); return; }                       // never stack on the cookie banner, see the guard above
      if (boxHasPicks()) { arm(); return; }                          // mid-build is not an abandoning shopper
      shown = true;
      try { window.sessionStorage.setItem("ioc-capture", "1"); } catch (e) {}
      dlg.showModal();
    }
    function arm() { clearTimeout(timer); timer = setTimeout(open, delay); }

    /* The banner can arrive AFTER the page settles: Shopify injects it once the consent-tracking feature loads, and
       the theme banner unhides on its own timer. A one-time check at load is not enough, so if a banner turns up
       while our modal is open, get out of its way and come back once consent has been dealt with. */
    function yieldToBanner() {
      if (!consentPending()) return;
      if (dlg.open) {
        dlg.close();
        shown = false;
        try { window.sessionStorage.removeItem("ioc-capture"); } catch (e) {}
      }
      arm();
    }
    new MutationObserver(yieldToBanner).observe(document.body, { childList: true, subtree: true });
    // Consent answered (Accept or Decline): the banner is gone, so restart the normal idle countdown.
    document.addEventListener("visitorConsentCollected", function () { arm(); });

    ["mousemove", "keydown", "scroll", "touchstart", "pointerdown"].forEach(function (ev) { document.addEventListener(ev, arm, { passive: true }); });
    arm();
    document.addEventListener("mouseout", function (e) {
      if (!e.relatedTarget && e.clientY <= 0 && window.innerWidth > 900) open();
    });
  }

  /* ---------- consent-gated marketing (Shopify Customer Privacy API) ----------
     The banner itself is Shopify's: Admin > Settings > Customer privacy > Cookie banner must be ON for the regions
     that need consent. This code only loads theme-added marketing scripts (Klaviyo id, SMS embed) once marketing
     consent is allowed, and re-checks when the visitor answers the banner. */
  function initConsent() {
    var cfgEl = document.querySelector("[data-ioc-config]");
    var cfg = {};
    try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : "{}"); } catch (e) {}
    var loaded = false;
    function inject(tpl) {
      var frag = tpl.content.cloneNode(true);
      Array.prototype.slice.call(frag.querySelectorAll("script")).forEach(function (old) {
        var s = document.createElement("script");
        Array.prototype.slice.call(old.attributes).forEach(function (a) { s.setAttribute(a.name, a.value); });
        s.textContent = old.textContent;
        old.replaceWith(s);
      });
      document.body.appendChild(frag);
    }
    function loadMarketing() {
      if (loaded) return;
      loaded = true;
      if (cfg.klaviyo) {
        var s = document.createElement("script");
        s.async = true;
        s.src = "https://static.klaviyo.com/onsite/js/" + encodeURIComponent(cfg.klaviyo) + "/klaviyo.js";
        document.head.appendChild(s);
      }
      document.querySelectorAll("template[data-ioc-marketing]").forEach(inject);
    }
    function check() {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return;
      var allowed = true;
      try {
        if (typeof cp.marketingAllowed === "function") allowed = cp.marketingAllowed();
        else if (typeof cp.userCanBeTracked === "function") allowed = cp.userCanBeTracked();
      } catch (e) {}
      /* GPC (Global Privacy Control): a signal-sending browser must never get Klaviyo loaded onsite, even once, on
         the first page or after navigating. The Shopify Customer Privacy API is supposed to fold GPC into
         marketingAllowed(), but there is a race on the very first page: consent-tracking-api can resolve before the
         API has read the GPC header, so marketingAllowed() briefly reports true. Read the browser signal directly
         as a second gate so that race can never ship a request. */
      try {
        if (navigator.globalPrivacyControl === true) allowed = false;
      } catch (e) {}
      if (allowed) loadMarketing();
    }
    document.addEventListener("visitorConsentCollected", function (e) {
      var d = e && e.detail ? e.detail : {};
      var gpcOn = false;
      try { gpcOn = navigator.globalPrivacyControl === true; } catch (err) {}
      if (!gpcOn && (d.marketingAllowed === true || d.marketingAllowed === "yes")) loadMarketing();
    });
    if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (err) { if (!err) check(); });
    } else {
      check();
    }
  }

  /* ---------- consent-banner layout (do not delete) ----------
     Shopify's hosted banner renders as a centred card covering the lower 40% of a 390px screen, on top of the hero
     CTA and the sticky buy bar. It is a legal control, so nothing here hides it, fades it, moves it off screen or
     shortens its life: assets/ioc-a11y.css re-lays it as a compact bottom bar with 44px targets, and this publishes
     its REAL rendered height as --ioc-consent-h so every other fixed element can sit clear of it. The variable is
     0px whenever no banner is up, so the offsets cost nothing once the visitor has answered. */
  function initConsentLayout() {
    var root = document.documentElement;
    var queued = false;
    function banner() {
      return document.querySelector(".shopify-pc__banner__dialog") || document.getElementById("shopify-pc__banner");
    }
    function measure() {
      queued = false;
      var el = banner();
      var up = onScreen(el);
      root.classList.toggle("ioc-consent-open", up);
      root.style.setProperty("--ioc-consent-h", up ? Math.ceil(el.getBoundingClientRect().height) + "px" : "0px");
    }
    // rAF-throttled: the banner is injected late and mutates while it animates in, so this runs on a busy observer.
    function schedule() { if (!queued) { queued = true; requestAnimationFrame(measure); } }
    measure();
    new MutationObserver(schedule).observe(root, {
      childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "hidden"]
    });
    window.addEventListener("resize", schedule, { passive: true });
    document.addEventListener("visitorConsentCollected", function () { setTimeout(measure, 60); });
  }

  ready(function () {
    initGiftNote();
    initCartGiftNote();
    initCapture();
    initConsent();
    initConsentLayout();
    // Horizon re-renders sections in the editor, on variant change, and on every cart update (Section Rendering
    // API redraws the drawer/cart-summary markup); rebind both gift-note flows whenever that happens.
    document.addEventListener("shopify:section:load", initGiftNote);
    document.addEventListener("shopify:section:load", initCartGiftNote);
    new MutationObserver(function () { initGiftNote(); initCartGiftNote(); }).observe(document.body, { childList: true, subtree: true });
  });
})();
